import { useEffect, useState } from 'react';
import { useTrafficStore } from '../stores/trafficStore';

const ChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const getSourceLabel = (type) => {
  const labels = {
    ref: 'Источник VK',
    hash: 'Hash параметр',
    direct: 'Прямая ссылка',
  };
  return labels[type] || type;
};

const getSourceBadgeColor = (type) => {
  const colors = {
    ref: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
    hash: 'bg-purple-600/20 text-purple-400 border-purple-600/30',
    direct: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
  };
  return colors[type] || 'bg-gray-600/20 text-gray-400 border-gray-600/30';
};

// Карточка метрики
const MetricCard = ({ title, value, subValue, color = 'accent' }) => {
  const colorClasses = {
    blue: 'from-blue-600/20 to-blue-600/5 border-blue-600/30',
    purple: 'from-purple-600/20 to-purple-600/5 border-purple-600/30',
    green: 'from-green-600/20 to-green-600/5 border-green-600/30',
    gray: 'from-gray-600/20 to-gray-600/5 border-gray-600/30',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color] || colorClasses.gray} rounded-xl border p-5`}>
      <div className="text-sm text-text-secondary mb-1">{title}</div>
      <div className="text-3xl font-bold text-text-primary">{value}</div>
      {subValue && <div className="text-xs text-text-muted mt-1">{subValue}</div>}
    </div>
  );
};

export const TrafficSources = () => {
  const { stats, list, dashboardSummary, isLoading, error, fetchStats, fetchList, fetchDashboardSummary } = useTrafficStore();
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchStats();
    fetchDashboardSummary();
  }, []);

  useEffect(() => {
    fetchList(page, limit, filterType);
  }, [page, filterType]);

  const handleFilterChange = (type) => {
    setFilterType(type === filterType ? null : type);
    setPage(1);
  };

  const formatSourceValue = (value) => {
    if (!value) return '—';
    if (value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    return value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error) {
    return (
      <div className="bg-error/10 border border-error rounded-xl p-4 text-error">
        <p className="font-medium">Ошибка загрузки данных</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Источники трафика</h1>
        <p className="text-text-secondary mt-1">Анализ источников, откуда приходят пользователи</p>
      </div>

      {/* Метрики */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Всего отслежено"
            value={stats.total_tracked}
            subValue={`из ${stats.total_users} пользователей`}
            color="blue"
          />
          {stats.by_type.map((item) => (
            <MetricCard
              key={item.source_type}
              title={getSourceLabel(item.source_type)}
              value={item.count}
              subValue={`${item.percentage}% от всех`}
              color={item.source_type === 'ref' ? 'purple' : item.source_type === 'hash' ? 'green' : 'gray'}
            />
          ))}
        </div>
      )}

      {/* Фильтры */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => handleFilterChange(null)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            !filterType
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          Все
        </button>
        {['ref', 'hash', 'direct'].map((type) => (
          <button
            key={type}
            onClick={() => handleFilterChange(type)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filterType === type
                ? 'bg-accent-primary text-white'
                : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
            }`}
          >
            {getSourceLabel(type)}
          </button>
        ))}
      </div>

      {/* Топ источники */}
      {stats && stats.top_sources && stats.top_sources.length > 0 && (
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Топ конкретные источники</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.top_sources.map((source, index) => (
              <div
                key={index}
                className="bg-bg-tertiary rounded-lg p-4 border border-border-color/50"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded border ${getSourceBadgeColor(source.source_type)}`}>
                    {getSourceLabel(source.source_type)}
                  </span>
                  <span className="text-sm font-bold text-text-primary">{source.count}</span>
                </div>
                <div className="text-sm text-text-secondary truncate" title={source.source_value}>
                  {formatSourceValue(source.source_value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Таблица пользователей */}
      <div className="bg-bg-secondary rounded-xl border border-border-color">
        <div className="p-6 border-b border-border-color">
          <h3 className="text-lg font-semibold text-text-primary">Пользователи по источникам</h3>
        </div>

        {isLoading && !list ? (
          <div className="p-8 flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" />
          </div>
        ) : list && list.items && list.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-bg-tertiary border-b border-border-color">
                  <tr>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">VK ID</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Источник</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Значение</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Дата регистрации</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Баланс</th>
                    <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Статус</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-color">
                  {list.items.map((item) => (
                    <tr key={item.vk_user_id} className="hover:bg-bg-tertiary/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-text-primary font-mono">
                        {item.vk_user_id}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-2 py-1 rounded border ${getSourceBadgeColor(item.source_type)}`}>
                          {getSourceLabel(item.source_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-text-secondary max-w-xs truncate">
                        {formatSourceValue(item.source_value)}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-secondary">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-text-primary">
                        {item.balance ?? '—'} ⭐
                      </td>
                      <td className="px-6 py-4">
                        {item.is_disabled ? (
                          <span className="text-xs px-2 py-1 rounded bg-error/20 text-error border border-error/30">
                            Заблокирован
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-success/20 text-success border border-success/30">
                            Активен
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {list.total_pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-border-color">
                <div className="text-sm text-text-secondary">
                  Страница {list.page} из {list.total_pages} ({list.total} записей)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(list.total_pages, p + 1))}
                    disabled={page >= list.total_pages}
                    className="p-2 rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-text-secondary">
            Нет данных об источниках трафика
          </div>
        )}
      </div>
    </div>
  );
};
